import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  clinicTimezoneLabel,
  clinicTimezoneSetup,
  dismissTimezoneNotice,
  timezoneNoticeDismissed,
} from "@/lib/clinic-timezone";
import type { SettingsTenant } from "@/types";

type TimezoneSetupNoticeProps = {
  settings: Pick<SettingsTenant, "timezone" | "timezone_confirmed_at"> | undefined;
  isAdmin: boolean;
  tenantId: string | null;
  /** Where focus goes once the notice (and its button) leaves the page. */
  onDismiss: () => void;
};

/**
 * SCHED-TZ-01 (OQ-48): Hoje tells the admin, once per session and clinic, that
 * the clinic timezone is missing or was never confirmed. Other roles cannot
 * change it, so they are not asked. The greeting is not touched: a timezone the
 * clinic has not confirmed is still the clinic's clock.
 */
export function TimezoneSetupNotice({ settings, isAdmin, tenantId, onDismiss }: TimezoneSetupNoticeProps) {
  const [dismissedHere, setDismissedHere] = useState<(string | null)[]>([]);
  const setup = clinicTimezoneSetup(settings);
  const dismissed = dismissedHere.includes(tenantId) || timezoneNoticeDismissed(tenantId);
  if (!isAdmin || !setup || dismissed) return null;

  const dismiss = () => {
    dismissTimezoneNotice(tenantId);
    setDismissedHere((current) => [...current, tenantId]);
    onDismiss();
  };

  return (
    <div
      role="status"
      aria-label="Fuso horário da clínica"
      className="flex flex-wrap items-center gap-3 rounded-xl border border-app-border bg-app-surface p-4 text-sm text-app-fg"
    >
      <Clock3 aria-hidden="true" className="size-4 shrink-0 text-brand" />
      <p className="min-w-0 flex-1">
        {setup === "missing" ? (
          <strong className="font-medium text-app-fg-strong">Fuso horário da clínica não definido</strong>
        ) : (
          <>
            <strong className="font-medium text-app-fg-strong">Confirme o fuso horário da clínica</strong>
            {` · A agenda usa ${clinicTimezoneLabel(settings?.timezone ?? "")}.`}
          </>
        )}
      </p>
      <Link
        to="/settings/clinic"
        className="font-medium text-brand underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        {setup === "missing" ? "Definir" : "Revisar"}
      </Link>
      <Button type="button" variant="ghost" size="icon" aria-label="Dispensar aviso do fuso horário" onClick={dismiss}>
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}
